import { useApi } from '@src/app/context/Api';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import React, { useState } from 'react';

import { Styles } from './includes/styles';

/**
 * CreateNote
 *
 * Displays a modal to create a note on the risk record.
 *
 * @param eid
 * @param handleSearch
 * @param isOpen
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function CreateNote({ eid, handleSearch, isOpen, setIsOpen }) {
  const [isCreating, setIsCreating] = useState(false);
  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordNoteUpdate] = useApiMutation('RiskRecordNoteUpdate');

  const defaultValues = {
    eid,
    note: '',
  };

  const form = useForm({ defaultValues });
  const { toast } = useToast();

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @returns {Promise<boolean>}
   */
  const handleSubmit = async (data) => {
    setIsCreating(true);
    await RiskRecordNoteUpdate(data, {
      onError: () => {
        toast.show({
          message: 'Unable to Create Note.',
          title: 'Create Note Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        setIsCreating(false);
        setIsOpen(false);
        await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] }]);
        handleSearch();
        toast.show({
          message: 'Note has been successfully created.',
          title: 'Created note to EID',
          variant: 'success',
        });
      },
    });
  };

  return (
    <ErrorHandler location="src/features/EidSearch/CreateNote/CreateNote.jsx">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          return setIsOpen(false);
        }}
        title="Create Note"
      >
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Styles>
              <Grid>
                <Grid.Col span={{ xs: 12 }}>
                  <TextInputArea label="Note" model="note" rows={3} validators={{ required: 'Please enter a note' }} />
                </Grid.Col>
              </Grid>
            </Styles>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button ariaLoadingLabel="Creating Note" isLoading={isCreating} type="submit" variant="solid">
                Create
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
